import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * 管理者ユーザー管理 callable hooks (Admin Wave AW3、docs/108 Critical #5,#6,#7 + High)。
 *
 * functions/src/admin/manageAdminUsers.ts の 4 callable (listAdmins / disableAdmin /
 * resetAdminPassword / inviteAdmin) を呼ぶ。すべて callable 側で `requireMaster` 強制。
 *
 * old2 apps/admin/src/pages/AdminUsersPage.tsx の呼出パターンを踏襲しつつ、v2 では一覧の真実源を
 * Firestore `adminUsers` collection ではなく Auth Custom Claims (listAdmins callable) に変更。
 */

export interface AdminUserRow {
  uid: string;
  email: string;
  role: 'master' | 'district';
  districts: string[];
  disabled: boolean;
  /** 削除保護対象 (初代マスター等)。UI は削除ボタンを無効化する。listAdmins が付与。 */
  protected?: boolean;
}

interface ListAdminsResult {
  admins: AdminUserRow[];
}

export interface DisableAdminInput {
  uid?: string;
  email?: string;
  disabled?: boolean;
}

interface DisableAdminResult {
  uid: string;
  disabled: boolean;
}

export interface DeleteAdminInput {
  uid?: string;
  email?: string;
}

interface DeleteAdminResult {
  uid: string;
  email: string;
}

export interface ResetAdminPasswordInput {
  email?: string;
  uid?: string;
  /** リンク有効期限 (日、1〜30、既定 7)。 */
  expiresInDays?: number;
}

export interface ResetAdminPasswordResult {
  resetLink: string;
  link: string;
  email: string;
  /** リンク有効期限 (ISO8601)。 */
  expiresAt: string;
}

export interface InviteAdminInput {
  email: string;
  role: 'master' | 'district';
  districts?: string[];
  /** 招待リンク有効期限 (日、1〜30、既定 7)。 */
  expiresInDays?: number;
}

export interface InviteAdminResult {
  uid: string;
  resetLink: string;
  link: string;
  email: string;
  role: 'master' | 'district';
  districts: string[];
  created: boolean;
  /** 招待リンク有効期限 (ISO8601)。 */
  expiresAt: string;
}

export type AdminUsersListState =
  | { status: 'idle'; admins: []; error: null }
  | { status: 'loading'; admins: AdminUserRow[]; error: null }
  | { status: 'ready'; admins: AdminUserRow[]; error: null }
  | { status: 'error'; admins: []; error: Error };

export interface UseAdminUsersReturn {
  state: AdminUsersListState;
  /** 一覧を (再) 取得。mutation 後に呼んで refresh する。 */
  refresh: () => Promise<void>;
  disableAdmin: (input: DisableAdminInput) => Promise<DisableAdminResult>;
  deleteAdmin: (input: DeleteAdminInput) => Promise<DeleteAdminResult>;
  resetAdminPassword: (input: ResetAdminPasswordInput) => Promise<ResetAdminPasswordResult>;
  inviteAdmin: (input: InviteAdminInput) => Promise<InviteAdminResult>;
}

/**
 * 管理者一覧 + mutation の集約 hook。
 *
 * 一覧は手動 refresh 方式 (listAdmins callable は subscribe できないため)。各 mutation は
 * Promise を resolve し、呼出側で toast / refresh を制御する。
 */
export function useAdminUsers(): UseAdminUsersReturn {
  const [state, setState] = useState<AdminUsersListState>({
    status: 'idle',
    admins: [],
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({
      status: 'loading',
      admins: prev.status === 'ready' || prev.status === 'loading' ? prev.admins : [],
      error: null,
    }));
    try {
      const fn = httpsCallable<unknown, ListAdminsResult>(functions, 'listAdmins');
      const res = await fn({});
      setState({ status: 'ready', admins: res.data.admins ?? [], error: null });
    } catch (e) {
      setState({ status: 'error', admins: [], error: e as Error });
    }
  }, []);

  const disableAdmin = useCallback(
    async (input: DisableAdminInput): Promise<DisableAdminResult> => {
      const fn = httpsCallable<DisableAdminInput, DisableAdminResult>(functions, 'disableAdmin');
      const res = await fn(input);
      return res.data;
    },
    [],
  );

  const deleteAdmin = useCallback(async (input: DeleteAdminInput): Promise<DeleteAdminResult> => {
    const fn = httpsCallable<DeleteAdminInput, DeleteAdminResult>(functions, 'deleteAdmin');
    const res = await fn(input);
    return res.data;
  }, []);

  const resetAdminPassword = useCallback(
    async (input: ResetAdminPasswordInput): Promise<ResetAdminPasswordResult> => {
      const fn = httpsCallable<ResetAdminPasswordInput, ResetAdminPasswordResult>(
        functions,
        'resetAdminPassword',
      );
      const res = await fn(input);
      return res.data;
    },
    [],
  );

  const inviteAdmin = useCallback(async (input: InviteAdminInput): Promise<InviteAdminResult> => {
    const fn = httpsCallable<InviteAdminInput, InviteAdminResult>(functions, 'inviteAdmin');
    const res = await fn(input);
    return res.data;
  }, []);

  return { state, refresh, disableAdmin, deleteAdmin, resetAdminPassword, inviteAdmin };
}
